import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw } from "lucide-react";
import BookCover from "@/components/landing/BookCover";

const QUESTIONS = [
  {
    q: "Où en es-tu dans ta mémorisation du Coran ?",
    options: ["Je débute tout juste", "J'ai déjà mémorisé une partie", "J'ai presque terminé"],
  },
  {
    q: "Ton objectif aujourd'hui ?",
    options: ["Mémoriser pas à pas", "Réviser pour ne plus oublier", "Les deux, dans la continuité"],
  },
  {
    q: "Ton plus grand défi ?",
    options: ["Rester régulier chaque jour", "Retenir durablement ce que je sais", "Avoir un plan complet de A à Z"],
  },
];

const RESULTS = {
  memorisation: {
    covers: ["mem"],
    badge: "Recommandé pour toi",
    title: "Guide de mémorisation",
    sub: "Le plan de 6 mois",
    price: "29,99 €",
    old: null,
    points: ["Un plan jour par jour pour démarrer sans te sentir dépassé", "Des techniques pour rester régulier, semaine après semaine"],
    cta: "Commencer ma mémorisation",
    link: "/commander?produit=memorisation",
  },
  revision: {
    covers: ["rev"],
    badge: "Recommandé pour toi",
    title: "Guide de révision",
    sub: "Le plan de 30 jours",
    price: "24,99 €",
    old: null,
    points: ["Un planning de 30 jours pour consolider ce que tu sais", "Des techniques pour ancrer durablement chaque verset"],
    cta: "Lancer ma révision",
    link: "/commander?produit=revision",
  },
  "pack-complet": {
    covers: ["mem", "rev"],
    badge: "Le plus choisi",
    title: "Pack Gold",
    sub: "Mémorisation + révision",
    price: "44,99 €",
    old: "54,98 €",
    points: ["Le parcours complet : tu mémorises, puis tu ancre", "9,99 € d'économie par rapport aux 2 guides séparés"],
    cta: "Obtenir le parcours complet",
    link: "/commander?produit=pack-complet",
  },
};

function recommend(answers) {
  if (answers[1] === 2 || answers[2] === 2) return "pack-complet";
  if (answers[1] === 1 || answers[0] === 2) return "revision";
  return "memorisation";
}

const EASE = [0.16, 1, 0.3, 1];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = step >= QUESTIONS.length;
  const result = done ? RESULTS[recommend(answers)] : null;

  const pick = (i) => {
    setAnswers([...answers, i]);
    setStep(step + 1);
  };

  const restart = () => {
    setAnswers([]);
    setStep(0);
  };

  return (
    <section id="quiz" className="quiz" data-testid="quiz-section">
      <div className="container">
        <motion.div
          className="section-head"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="eyebrow">Le quiz</div>
          <h2>Quel guide est fait pour toi ?</h2>
          <p>Réponds à 3 questions et reçois une recommandation claire, adaptée à ton parcours.</p>
        </motion.div>

        <motion.div
          className="quiz-card"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                <div className="quiz-progress">
                  <span data-testid="quiz-progress-label">Question {step + 1} sur {QUESTIONS.length}</span>
                  <div className="quiz-dots">
                    {QUESTIONS.map((_, i) => (
                      <span key={i} className={`quiz-dot ${i <= step ? "on" : ""}`} />
                    ))}
                  </div>
                </div>
                <h3 className="quiz-q" data-testid="quiz-question">{QUESTIONS[step].q}</h3>
                {QUESTIONS[step].options.map((opt, i) => (
                  <button
                    key={i}
                    className="quiz-opt"
                    onClick={() => pick(i)}
                    data-testid={`quiz-option-${i}`}
                  >
                    {opt}
                  </button>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="result"
                className="quiz-result"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="quiz-result-card"
              >
                <div className="quiz-covers">
                  {result.covers.map((c) => (
                    <BookCover key={c} variant={c} mini />
                  ))}
                </div>
                <span className="quiz-badge">{result.badge}</span>
                <h3 className="quiz-result-title">{result.title}</h3>
                <p className="quiz-result-sub">{result.sub}</p>
                <div className="quiz-price">
                  {result.old && <span className="quiz-old">{result.old}</span>}
                  <span>{result.price}</span>
                </div>
                <ul className="quiz-points">
                  {result.points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
                <Link to={result.link} className="btn" data-testid="quiz-result-cta">
                  {result.cta}
                </Link>
                <button className="quiz-restart" onClick={restart} data-testid="quiz-restart-btn">
                  <RotateCcw size={14} /> Recommencer le quiz
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
